/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import { useLoading, usePersistedTransactionId } from '../context';
import { ScanIcon } from './Icons';
import { IconButton } from './Button';
import { Stack, styled } from '@mui/material';

const MAX_FILE_SIZE_MB = 10;
const TIMEOUT_MS = 20 * 1000;

const FullInvoiceUploadBox = styled(Stack)`
  position: relative;
  flex-direction: column;
  align-items: flex-end;
  height: 60px;
`;

const CustomFileInput = styled('div')`
  display: block;
  padding: 7px 10px;
  background-color: #b34cff;
  font-size: 15px;
  color: white;
  border-radius: 5px;
  cursor: pointer;
  text-align: center;
  &:hover {
    background-color: #a54cff;
  }
`;

const FileNameDisplay = styled('span')`
  font-size: 14px;
  color: #333;
  position: absolute;
  bottom: 0;
  right: 0;
  white-space: nowrap;
`;

const sendImageRecognizeRequest = async (
  file,
  hideLoading,
  persistedTransactionId
) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('persistedTransactionId', persistedTransactionId);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const response = await fetch('/api/image-recoginze', {
      method: 'POST',
      body: formData,
      signal: controller.signal,
    });

    if (!response.ok) {
      hideLoading(`File failed to upload: ${file.name}`, 'error');
      return {};
    }

    hideLoading(`File successful uploaded!`, 'success');
    const result = await response.json();
    if (result && !result.billerCode && !result.reference) {
      hideLoading(
        `It's unfortunate that we didn't find useful info from it`,
        'warn'
      );
    }
    return result || {};
  } catch (err) {
    if (err.name === 'AbortError') {
      hideLoading(
        'Request timed out, please try again or change another file',
        'error'
      );
    } else {
      hideLoading(`File failed to upload: ${file.name}`, 'error');
    }
    return {};
  } finally {
    clearTimeout(timeoutId);
  }
};

export const ScanImageWithTextRecognition = ({
  onScanImageFinished,
  forFullInvoice = false,
}) => {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const { showLoading, hideLoading } = useLoading();
  const persistedTransactionId = usePersistedTransactionId();

  useEffect(() => {
    const recognizeText = async () => {
      if (selectedFile) {
        showLoading();

        const apiImageRecognizeResponse = await sendImageRecognizeRequest(
          selectedFile,
          hideLoading,
          persistedTransactionId
        );
        await onScanImageFinished({
          billerCode: apiImageRecognizeResponse.billerCode,
          refNumber: apiImageRecognizeResponse.reference,
          amount: apiImageRecognizeResponse.amount,
          filePath: apiImageRecognizeResponse.filePath,
        });
      }
    };

    if (selectedFile) {
      const fileSizeMB = selectedFile.size / (1024 * 1024); // Convert size to MB
      if (fileSizeMB > MAX_FILE_SIZE_MB) {
        hideLoading(
          `File size exceeds ${MAX_FILE_SIZE_MB} MB, please upload a smaller one.`,
          'error'
        );
      } else {
        recognizeText();
      }
    }
  }, [selectedFile]);

  const handleOpenFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      console.log('File selected: ', file);
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        onChange={handleFileChange}
        type="file"
        accept="image/jpeg,image/png,image/tiff,image/bmp,application/pdf"
        style={{ display: 'none' }}
        id="uploadBill"
      />
      {!forFullInvoice && (
        <IconButton onClick={handleOpenFileDialog}>
          <ScanIcon size="20px" />
        </IconButton>
      )}
      {forFullInvoice && (
        <FullInvoiceUploadBox>
          <CustomFileInput onClick={handleOpenFileDialog}>
            上传原始账单
          </CustomFileInput>
          {selectedFile && (
            <FileNameDisplay>{selectedFile.name}</FileNameDisplay>
          )}
        </FullInvoiceUploadBox>
      )}
    </>
  );
};
